import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { ClipLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import {
  companyManagementUrl,
  customerservice,
  userManagementUrl,
} from "../../config";
import {
  fetchJson,
  fetchJsonDynamic,
} from "../../data/DynamicServiceNew/jsondynamicservice";
import { tokens } from "../../theme";
// Create modern, sleek Leaflet map markers
const getModernMarkerIcon = (color, isApproved) => {
  return L.divIcon({
    className: "modern-map-marker",
    html: `
      <div style="
        position: relative;
        width: 30px;
        height: 38px;
        display: flex;
        flex-direction: column;
        align-items: center;
        filter: drop-shadow(0 4px 6px rgba(0,0,0,0.3));
        cursor: pointer;
        transition: transform 0.2s ease;
      ">
        <div style="
          width: 28px;
          height: 28px;
          background: ${color};
          border: 2.5px solid #ffffff;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: inset 0 2px 4px rgba(255,255,255,0.4);
        ">
          <div style="
            width: 10px;
            height: 10px;
            background: #ffffff;
            border-radius: 50%;
            transform: rotate(45deg);
          "></div>
        </div>
        <div style="
          width: 6px;
          height: 3px;
          background: rgba(0,0,0,0.35);
          border-radius: 50%;
          margin-top: 2px;
        "></div>
      </div>
    `,
    iconSize: [30, 38],
    iconAnchor: [15, 36],
    popupAnchor: [0, -36],
  });
};
const getStatusBadgeConfig = (isApproved) => {
  if (isApproved === 2) {
    return {
      label: "Approved",
      color: "#10b981",
      bgColor: "rgba(16, 185, 129, 0.15)",
      borderColor: "rgba(16, 185, 129, 0.35)",
    };
  } else {
    return {
      label: "Awaiting Approval",
      color: "#f59e0b",
      bgColor: "rgba(245, 158, 11, 0.15)",
      borderColor: "rgba(245, 158, 11, 0.35)",
    };
  }
};
const UpdateMapCenter = ({ coordinates }) => {
  const map = useMap();
  useEffect(() => {
    if (coordinates && coordinates[0] && coordinates[1]) {
      map.setView(coordinates, map.getZoom(), { animate: true });
    }
  }, [coordinates, map]);
  return null;
};
const MapComponent = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const decodedToken = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("decodedToken")) || {};
    } catch {
      return {};
    }
  }, []);
  const RoleGroupId = parseInt(decodedToken.RoleGroupId || 0);
  const tokenRegionId = parseInt(decodedToken.RegionId || 0);
  const tokenBranchId = parseInt(decodedToken.BranchId || 0);
  const [defaultPosition, setDefaultPosition] = useState([-0.70732, 36.45483]);
  const [CustomerData, setCustomerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [regionData, setRegionData] = useState([]);
  const [branchData, setBranchData] = useState([]);
  const [foData, setFoData] = useState([]);
  // Filters State
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedFo, setSelectedFo] = useState("");

  // Space-saving UI States
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [isBarMinimized, setIsBarMinimized] = useState(false);
  const [isLegendOpen, setIsLegendOpen] = useState(false);

  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "2", label: "Approved" },
    { value: "1", label: "Awaiting Approval" },
  ];
  const handleView360 = (Id) => {
    if (!Id) return;
    localStorage.setItem("CustomerId", Id);
    navigate("/customer-360");
  };
  // Fetch initial regions list
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const regionResponse = await fetchJson({
          objectName: "region",
          baseUrl: companyManagementUrl.uri,
        });
        if (regionResponse?.results) {
          const allRegions = JSON.parse(regionResponse.results) || [];
          setRegionData(allRegions);
        }
      } catch (err) {
        console.error("Failed to load regions:", err);
      }
    };
    fetchRegions();
  }, []);

  // Fetch branches helper
  const fetchBranches = async (regionId) => {
    if (!regionId) return [];
    try {
      const params = { regionId: parseInt(regionId, 10) };
      const businessDetails = await fetchJsonDynamic(
        "allBranchesByColumnValues",
        "branchModel",
        params,
        companyManagementUrl.uri
      );
      const allBranchDetails = JSON.parse(businessDetails?.results || "[]");
      return allBranchDetails.map((b) => ({
        value: b.Id,
        label: b.Name,
      }));
    } catch (error) {
      console.error("Error fetching branches:", error);
      return [];
    }
  };
  // Fetch Field Officers helper
  const fetchFieldOfficers = async (branchId) => {
    if (!branchId) return [];
    try {
      const params = { branchId: parseInt(branchId, 10) };
      const businessDetails = await fetchJsonDynamic(
        "allUsersByColumnValues",
        "usermodel",
        params,
        userManagementUrl.uri
      );
      const userList = JSON.parse(businessDetails?.results || "[]");
      return userList.map((u) => ({
        value: u.Id,
        label: `${u.FirstName || ""} ${u.LastName || ""}`.trim() || `User #${u.Id}`,
      }));
    } catch (error) {
      console.error("Error fetching field officers:", error);
      return [];
    }
  };

  useEffect(() => {
    if (RoleGroupId === 4 && tokenRegionId) {
      setSelectedRegion(String(tokenRegionId));
      fetchBranches(tokenRegionId).then(setBranchData);
    } else if (RoleGroupId === 5 && tokenBranchId) {
      setSelectedBranch(String(tokenBranchId));
      fetchFieldOfficers(tokenBranchId).then(setFoData);
    }
  }, [RoleGroupId, tokenRegionId, tokenBranchId]);
  // Main Customer Fetching with Redis & Fallback
  useEffect(() => {
    let isCancelled = false;
    const fetchCustomer = async () => {
      setLoading(true);
      try {
        const cleanParams = {};
        if (selectedRegion) cleanParams.regionId = parseInt(selectedRegion, 10);
        if (selectedBranch) cleanParams.branchId = parseInt(selectedBranch, 10);
        if (selectedFo) cleanParams.fieldOfficerId = parseInt(selectedFo, 10);
        if (selectedStatus !== "" && selectedStatus !== null && selectedStatus !== undefined) {
          cleanParams.isApproved = parseInt(selectedStatus, 10);
        }
        const hasFilterParams = Object.keys(cleanParams).length > 0;
        // Try dedicated Redis-cached query first
        let response = await fetchJson({
          objectName: "customersForMap",
          baseUrl: customerservice.uri,
          inputObjectName: "customer",
          params: hasFilterParams ? cleanParams : null,
        });
        // Fallback to customersByColumnValues if customersForMap is not yet available
        // if (!response || response.error || !response.results) {
        //   response = await fetchJson({
        //     objectName: "customersByColumnValues",
        //     baseUrl: customerservice.uri,
        //     inputObjectName: "customer",
        //     params: hasFilterParams ? cleanParams : null,
        //   });
        // }
        if (isCancelled || !response || !response.results) {
          setLoading(false);
          return;
        }
        const rawData = JSON.parse(response.results) || [];
        const mappedData = (Array.isArray(rawData) ? rawData : [])
          .filter((f) => f.Latitude != null && f.Longitude != null)
          .map((farmer) => {
            const lat = parseFloat(farmer.Latitude);
            const lng = parseFloat(farmer.Longitude);
            return {
              coordinates: [lat, lng],
              customerFirstName: farmer.FirstName || "",
              customerLastName: farmer.OtherNames || "",
              CustomerId: farmer.Id,
              customerPhone: farmer.MobileNumber || "N/A",
              isApproved: farmer.IsApproved,
              branchName: farmer.BranchName || "",
              regionName: farmer.RegionName || "",
              accountBalance: farmer.AccountBalance,
              loanBalance: farmer.LoanBalance,
            };
          })
          .filter(
            (c) =>
              !isNaN(c.coordinates[0]) &&
              !isNaN(c.coordinates[1]) &&
              !(c.coordinates[0] === 0 && c.coordinates[1] === 0)
          );
        setCustomerData(mappedData);
      } catch (err) {
        console.error("Error loading map customers:", err);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };
    fetchCustomer();

    return () => {
      isCancelled = true;
    };
  }, [selectedRegion, selectedBranch, selectedFo, selectedStatus]);
  // Handlers
  const handleRegionChange = async (val) => {
    setSelectedRegion(val);
    setSelectedBranch("");
    setSelectedFo("");
    setBranchData([]);
    setFoData([]);
    if (val) {
      const branches = await fetchBranches(parseInt(val, 10));
      setBranchData(branches);
    }
  };
  const handleBranchChange = async (val) => {
    setSelectedBranch(val);
    setSelectedFo("");
    setFoData([]);
    if (val) {
      const officers = await fetchFieldOfficers(parseInt(val, 10));
      setFoData(officers);
    }
  };
  const handleFoChange = (val) => {
    setSelectedFo(val);
  };
  const handleStatusChange = (val) => {
    setSelectedStatus(val);
  };

  const handleResetFilters = () => {
    setSelectedStatus("");
    if (RoleGroupId <= 3) {
      setSelectedRegion("");
      setSelectedBranch("");
      setSelectedFo("");
      setBranchData([]);
      setFoData([]);
    } else if (RoleGroupId === 4) {
      setSelectedBranch("");
      setSelectedFo("");
      setFoData([]);
    } else if (RoleGroupId === 5) {
      setSelectedFo("");
    }
  };
  // Center map on the first customer if available
  const firstValidCoordinate = CustomerData?.[0]?.coordinates;
  useEffect(() => {
    if (firstValidCoordinate) {
      setDefaultPosition(firstValidCoordinate);
    }
  }, [firstValidCoordinate]);

  const approvedCount = useMemo(
    () => CustomerData.filter((c) => c.isApproved === 2).length,
    [CustomerData]
  );
  const pendingCount = useMemo(
    () => CustomerData.filter((c) => c.isApproved !== 2).length,
    [CustomerData]
  );

  const handleClearRegion = () => {
    setSelectedRegion("");
    setSelectedBranch("");
    setSelectedFo("");
    setBranchData([]);
    setFoData([]);
  };

  const handleClearBranch = () => {
    setSelectedBranch("");
    setSelectedFo("");
    setFoData([]);
  };

  const handleClearFo = () => {
    setSelectedFo("");
  };

  const selectedRegionName = useMemo(() => {
    if (!selectedRegion) return "";
    const found = regionData.find((r) => String(r.Id) === String(selectedRegion));
    return found ? found.Name : `Region #${selectedRegion}`;
  }, [selectedRegion, regionData]);

  const selectedBranchName = useMemo(() => {
    if (!selectedBranch) return "";
    const found = branchData.find((b) => String(b.value) === String(selectedBranch));
    return found ? found.label : `Branch #${selectedBranch}`;
  }, [selectedBranch, branchData]);

  const selectedFoName = useMemo(() => {
    if (!selectedFo) return "";
    const found = foData.find((f) => String(f.value) === String(selectedFo));
    return found ? found.label : `Officer #${selectedFo}`;
  }, [selectedFo, foData]);

  const activeAdvancedCount = useMemo(() => {
    let count = 0;
    if (selectedRegion) count++;
    if (selectedBranch) count++;
    if (selectedFo) count++;
    return count;
  }, [selectedRegion, selectedBranch, selectedFo]);

  const hasAnyFilterActive = useMemo(() => {
    return Boolean(
      selectedStatus !== "" ||
      selectedRegion ||
      selectedBranch ||
      selectedFo
    );
  }, [selectedStatus, selectedRegion, selectedBranch, selectedFo]);

  const hasAdvancedFilterOptions = [1, 2, 3, 4, 5].includes(RoleGroupId);
  return (
    <div
      style={{
        position: "relative",
        height: "calc(100vh - 75px)",
        width: "100%",
        overflow: "hidden",
        backgroundColor: isDark ? "#0d1117" : "#f8fafc",
      }}
    > {/* Floating Glassmorphic Filter Bar */}
      {/* Floating Glassmorphic Control Island */}
      {isBarMinimized ? (
        <div
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "14px",
            left: "14px",
            zIndex: 1000,
            background: isDark
              ? "rgba(15, 23, 42, 0.92)"
              : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderRadius: "12px",
            border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(226, 232, 240, 0.9)"
              }`,
            boxShadow: isDark
              ? "0 10px 25px -5px rgba(0, 0, 0, 0.5)"
              : "0 10px 25px -5px rgba(15, 23, 42, 0.15)",
            padding: "6px 12px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            transition: "all 0.25s ease",
          }}
        >
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              boxShadow: "0 2px 8px rgba(37, 99, 235, 0.35)",
            }}
          >
            <LocationOnIcon style={{ fontSize: "16px" }} />
          </div>
          <div>
            <Typography
              style={{
                fontWeight: 700,
                fontSize: "13px",
                lineHeight: 1.2,
                color: isDark ? "#f1f5f9" : "#0f172a",
              }}
            >
              Customer Map
            </Typography>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Typography
                variant="caption"
                style={{
                  color: isDark ? "#94a3b8" : "#64748b",
                  fontSize: "11px",
                  fontWeight: 600,
                }}
              >
                {CustomerData.length} Customers
              </Typography>
              {loading && (
                <ClipLoader size={12} color="#3b82f6" loading={true} />
              )}
            </div>
          </div>

          {hasAnyFilterActive && (
            <span
              style={{
                fontSize: "11px",
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: "10px",
                backgroundColor: isDark
                  ? "rgba(59, 130, 246, 0.2)"
                  : "rgba(59, 130, 246, 0.12)",
                color: isDark ? "#60a5fa" : "#2563eb",
                border: "1px solid rgba(59, 130, 246, 0.3)",
              }}
            >
              Filtered
            </span>
          )}

          <Tooltip title="Expand control bar">
            <button
              type="button"
              onClick={() => setIsBarMinimized(false)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "5px 10px",
                borderRadius: "8px",
                border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.15)" : "#cbd5e1"
                  }`,
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.08)"
                  : "#f1f5f9",
                color: isDark ? "#e2e8f0" : "#334155",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <span>Controls</span>
              <ExpandMoreIcon style={{ fontSize: "16px" }} />
            </button>
          </Tooltip>
        </div>
      ) : (
        <div
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "12px",
            left: "14px",
            right: "14px",
            maxWidth: "1060px",
            margin: "0 auto",
            zIndex: 1000,
            background: isDark
              ? "rgba(15, 23, 42, 0.9)"
              : "rgba(255, 255, 255, 0.94)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            borderRadius: "14px",
            border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(226, 232, 240, 0.9)"
              }`,
            boxShadow: isDark
              ? "0 10px 30px -5px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)"
              : "0 10px 30px -5px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.04)",
            padding: "8px 14px",
            display: "flex",
            flexDirection: "column",
            gap: isFilterExpanded ? "8px" : "6px",
            transition: "all 0.25s ease",
          }}
        >
          {/* Main Compact Row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            {/* Left: Brand & Count */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "8px",
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ffffff",
                  boxShadow: "0 2px 8px rgba(37, 99, 235, 0.35)",
                  flexShrink: 0,
                }}
              >
                <LocationOnIcon style={{ fontSize: "17px" }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Typography
                  style={{
                    fontWeight: 700,
                    fontSize: "13.5px",
                    color: isDark ? "#f1f5f9" : "#0f172a",
                    letterSpacing: "-0.01em",
                    whiteSpace: "nowrap",
                  }}
                >
                  Customer Map
                </Typography>
                <Chip
                  label={`${CustomerData.length} Customers`}
                  size="small"
                  style={{
                    backgroundColor: isDark
                      ? "rgba(59, 130, 246, 0.15)"
                      : "rgba(59, 130, 246, 0.1)",
                    color: isDark ? "#60a5fa" : "#2563eb",
                    border: "1px solid rgba(59, 130, 246, 0.3)",
                    fontWeight: 600,
                    fontSize: "11px",
                    height: "22px",
                  }}
                />
                <Chip
                  icon={
                    <FlashOnIcon style={{ fontSize: "12px", color: "#f59e0b" }} />
                  }
                  label="Redis"
                  size="small"
                  style={{
                    backgroundColor: isDark
                      ? "rgba(245, 158, 11, 0.15)"
                      : "rgba(245, 158, 11, 0.1)",
                    color: isDark ? "#fbbf24" : "#b45309",
                    border: "1px solid rgba(245, 158, 11, 0.3)",
                    fontWeight: 600,
                    fontSize: "10.5px",
                    height: "22px",
                  }}
                />
                {loading && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      marginLeft: "4px",
                    }}
                  >
                    <ClipLoader size={12} color="#3b82f6" loading={true} />
                    <Typography
                      variant="caption"
                      style={{
                        color: isDark ? "#94a3b8" : "#64748b",
                        fontSize: "10.5px",
                      }}
                    >
                      Syncing...
                    </Typography>
                  </div>
                )}
              </div>
            </div>

            {/* Center: Segmented Status Controls */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.04)",
                padding: "2px",
                borderRadius: "10px",
                border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"
                  }`,
                gap: "2px",
              }}
            >
              <button
                type="button"
                onClick={() => handleStatusChange("")}
                style={{
                  padding: "4px 9px",
                  borderRadius: "8px",
                  border: "none",
                  fontSize: "11.5px",
                  fontWeight: selectedStatus === "" ? 700 : 500,
                  cursor: "pointer",
                  backgroundColor:
                    selectedStatus === ""
                      ? isDark
                        ? "#2563eb"
                        : "#3b82f6"
                      : "transparent",
                  color:
                    selectedStatus === ""
                      ? "#ffffff"
                      : isDark
                        ? "#94a3b8"
                        : "#64748b",
                  transition: "all 0.15s ease",
                  whiteSpace: "nowrap",
                }}
              >
                All ({CustomerData.length})
              </button>
              <button
                type="button"
                onClick={() => handleStatusChange("2")}
                style={{
                  padding: "4px 9px",
                  borderRadius: "8px",
                  border: "none",
                  fontSize: "11.5px",
                  fontWeight: selectedStatus === "2" ? 700 : 500,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  backgroundColor:
                    selectedStatus === "2" ? "#10b981" : "transparent",
                  color:
                    selectedStatus === "2"
                      ? "#ffffff"
                      : isDark
                        ? "#94a3b8"
                        : "#64748b",
                  transition: "all 0.15s ease",
                  whiteSpace: "nowrap",
                }}
              >
                <span
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    backgroundColor:
                      selectedStatus === "2" ? "#ffffff" : "#10b981",
                  }}
                />
                Approved ({approvedCount})
              </button>
              <button
                type="button"
                onClick={() => handleStatusChange("1")}
                style={{
                  padding: "4px 9px",
                  borderRadius: "8px",
                  border: "none",
                  fontSize: "11.5px",
                  fontWeight: selectedStatus === "1" ? 700 : 500,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  backgroundColor:
                    selectedStatus === "1" ? "#f59e0b" : "transparent",
                  color:
                    selectedStatus === "1"
                      ? "#ffffff"
                      : isDark
                        ? "#94a3b8"
                        : "#64748b",
                  transition: "all 0.15s ease",
                  whiteSpace: "nowrap",
                }}
              >
                <span
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    backgroundColor:
                      selectedStatus === "1" ? "#ffffff" : "#f59e0b",
                  }}
                />
                Pending ({pendingCount})
              </button>
            </div>

            {/* Right: Actions (Filters Toggle, Reset, Minimize) */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {hasAdvancedFilterOptions && (
                <button
                  type="button"
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "5px 10px",
                    borderRadius: "8px",
                    border: `1px solid ${activeAdvancedCount > 0
                        ? "#3b82f6"
                        : isDark
                          ? "rgba(255, 255, 255, 0.15)"
                          : "#cbd5e1"
                      }`,
                    backgroundColor:
                      isFilterExpanded || activeAdvancedCount > 0
                        ? isDark
                          ? "rgba(59, 130, 246, 0.18)"
                          : "rgba(59, 130, 246, 0.1)"
                        : isDark
                          ? "rgba(255, 255, 255, 0.05)"
                          : "#ffffff",
                    color:
                      activeAdvancedCount > 0
                        ? isDark
                          ? "#60a5fa"
                          : "#2563eb"
                        : isDark
                          ? "#e2e8f0"
                          : "#334155",
                    fontSize: "12px",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  <FilterAltIcon style={{ fontSize: "14px" }} />
                  <span>Filters</span>
                  {activeAdvancedCount > 0 && (
                    <span
                      style={{
                        backgroundColor: "#3b82f6",
                        color: "#ffffff",
                        borderRadius: "10px",
                        padding: "1px 6px",
                        fontSize: "10px",
                        fontWeight: 700,
                      }}
                    >
                      {activeAdvancedCount}
                    </span>
                  )}
                  {isFilterExpanded ? (
                    <ExpandLessIcon style={{ fontSize: "16px" }} />
                  ) : (
                    <ExpandMoreIcon style={{ fontSize: "16px" }} />
                  )}
                </button>
              )}

              {/* Reset button */}
              {hasAnyFilterActive && (
                <Tooltip title="Reset all filters">
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "5px 9px",
                      borderRadius: "8px",
                      border: `1px solid ${isDark ? "rgba(239, 68, 68, 0.3)" : "#fca5a5"
                        }`,
                      backgroundColor: isDark
                        ? "rgba(239, 68, 68, 0.12)"
                        : "#fef2f2",
                      color: isDark ? "#f87171" : "#dc2626",
                      fontSize: "12px",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <RestartAltIcon style={{ fontSize: "14px" }} />
                    <span>Reset</span>
                  </button>
                </Tooltip>
              )}

              {/* Minimize bar button */}
              <Tooltip title="Minimize toolbar">
                <IconButton
                  size="small"
                  onClick={() => setIsBarMinimized(true)}
                  style={{
                    padding: "4px",
                    borderRadius: "8px",
                    color: isDark ? "#94a3b8" : "#64748b",
                    backgroundColor: isDark
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.04)",
                  }}
                >
                  <ExpandLessIcon style={{ fontSize: "18px" }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          {/* Active Filter Badges (Shown when drawer is closed to maintain awareness) */}
          {!isFilterExpanded && activeAdvancedCount > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "6px",
                paddingTop: "2px",
              }}
            >
              <Typography
                variant="caption"
                style={{
                  color: isDark ? "#94a3b8" : "#64748b",
                  fontSize: "11px",
                  fontWeight: 600,
                }}
              >
                Filtered by:
              </Typography>
              {selectedRegion && (
                <Chip
                  size="small"
                  label={`Region: ${selectedRegionName}`}
                  onDelete={handleClearRegion}
                  deleteIcon={
                    <CloseIcon style={{ fontSize: "13px !important" }} />
                  }
                  style={{
                    height: "22px",
                    fontSize: "11px",
                    fontWeight: 600,
                    backgroundColor: isDark
                      ? "rgba(59, 130, 246, 0.15)"
                      : "rgba(59, 130, 246, 0.1)",
                    color: isDark ? "#93c5fd" : "#1d4ed8",
                    border: "1px solid rgba(59, 130, 246, 0.3)",
                  }}
                />
              )}
              {selectedBranch && (
                <Chip
                  size="small"
                  label={`Branch: ${selectedBranchName}`}
                  onDelete={handleClearBranch}
                  deleteIcon={
                    <CloseIcon style={{ fontSize: "13px !important" }} />
                  }
                  style={{
                    height: "22px",
                    fontSize: "11px",
                    fontWeight: 600,
                    backgroundColor: isDark
                      ? "rgba(59, 130, 246, 0.15)"
                      : "rgba(59, 130, 246, 0.1)",
                    color: isDark ? "#93c5fd" : "#1d4ed8",
                    border: "1px solid rgba(59, 130, 246, 0.3)",
                  }}
                />
              )}
              {selectedFo && (
                <Chip
                  size="small"
                  label={`FO: ${selectedFoName}`}
                  onDelete={handleClearFo}
                  deleteIcon={
                    <CloseIcon style={{ fontSize: "13px !important" }} />
                  }
                  style={{
                    height: "22px",
                    fontSize: "11px",
                    fontWeight: 600,
                    backgroundColor: isDark
                      ? "rgba(59, 130, 246, 0.15)"
                      : "rgba(59, 130, 246, 0.1)",
                    color: isDark ? "#93c5fd" : "#1d4ed8",
                    border: "1px solid rgba(59, 130, 246, 0.3)",
                  }}
                />
              )}
            </div>
          )}

          {/* Expandable Advanced Filter Drawer */}
          {isFilterExpanded && (
            <div
              style={{
                borderTop: `1px solid ${isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.07)"
                  }`,
                paddingTop: "8px",
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(180px, 1fr)) auto",
                gap: "8px",
                alignItems: "center",
              }}
            >
              {/* Region Dropdown (Roles 1, 2) */}
              {[1, 2].includes(RoleGroupId) && (
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "10px",
                      fontWeight: 700,
                      color: isDark ? "#94a3b8" : "#64748b",
                      marginBottom: "2px",
                      letterSpacing: "0.04em",
                    }}
                  >
                    REGION
                  </label>
                  <select
                    value={selectedRegion}
                    onChange={(e) => handleRegionChange(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "5px 10px",
                      borderRadius: "8px",
                      fontSize: "12px",
                      fontWeight: 500,
                      border: `1px solid ${selectedRegion
                          ? "#3b82f6"
                          : isDark
                            ? "rgba(255, 255, 255, 0.15)"
                            : "#cbd5e1"
                        }`,
                      backgroundColor: isDark ? "#1e293b" : "#ffffff",
                      color: isDark ? "#f1f5f9" : "#0f172a",
                      outline: "none",
                      cursor: "pointer",
                    }}
                  >
                    <option value="">All Regions</option>
                    {regionData.map((reg) => (
                      <option
                        key={reg.Id}
                        value={reg.Id}
                        style={{
                          backgroundColor: isDark ? "#1e293b" : "#ffffff",
                          color: isDark ? "#f1f5f9" : "#0f172a",
                        }}
                      >
                        {reg.Name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Branch Dropdown (Roles 1, 2, 3) */}
              {[1, 2, 3].includes(RoleGroupId) && (
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "10px",
                      fontWeight: 700,
                      color: isDark ? "#94a3b8" : "#64748b",
                      marginBottom: "2px",
                      letterSpacing: "0.04em",
                    }}
                  >
                    BRANCH
                  </label>
                  <select
                    value={selectedBranch}
                    onChange={(e) => handleBranchChange(e.target.value)}
                    disabled={Boolean(selectedRegion && branchData.length === 0)}
                    style={{
                      width: "100%",
                      padding: "5px 10px",
                      borderRadius: "8px",
                      fontSize: "12px",
                      fontWeight: 500,
                      border: `1px solid ${selectedBranch
                          ? "#3b82f6"
                          : isDark
                            ? "rgba(255, 255, 255, 0.15)"
                            : "#cbd5e1"
                        }`,
                      backgroundColor: isDark ? "#1e293b" : "#ffffff",
                      color: isDark ? "#f1f5f9" : "#0f172a",
                      outline: "none",
                      cursor: "pointer",
                      opacity:
                        Boolean(selectedRegion && branchData.length === 0)
                          ? 0.6
                          : 1,
                    }}
                  >
                    <option value="">All Branches</option>
                    {branchData.map((branch) => (
                      <option
                        key={branch.value}
                        value={branch.value}
                        style={{
                          backgroundColor: isDark ? "#1e293b" : "#ffffff",
                          color: isDark ? "#f1f5f9" : "#0f172a",
                        }}
                      >
                        {branch.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Field Officer Dropdown (Roles 1, 2, 3, 4, 5) */}
              {[1, 2, 3, 4, 5].includes(RoleGroupId) && (
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "10px",
                      fontWeight: 700,
                      color: isDark ? "#94a3b8" : "#64748b",
                      marginBottom: "2px",
                      letterSpacing: "0.04em",
                    }}
                  >
                    FIELD OFFICER
                  </label>
                  <select
                    value={selectedFo}
                    onChange={(e) => handleFoChange(e.target.value)}
                    disabled={Boolean(selectedBranch && foData.length === 0)}
                    style={{
                      width: "100%",
                      padding: "5px 10px",
                      borderRadius: "8px",
                      fontSize: "12px",
                      fontWeight: 500,
                      border: `1px solid ${selectedFo
                          ? "#3b82f6"
                          : isDark
                            ? "rgba(255, 255, 255, 0.15)"
                            : "#cbd5e1"
                        }`,
                      backgroundColor: isDark ? "#1e293b" : "#ffffff",
                      color: isDark ? "#f1f5f9" : "#0f172a",
                      outline: "none",
                      cursor: "pointer",
                      opacity:
                        Boolean(selectedBranch && foData.length === 0)
                          ? 0.6
                          : 1,
                    }}
                  >
                    <option value="">All Field Officers</option>
                    {foData.map((fo) => (
                      <option
                        key={fo.value}
                        value={fo.value}
                        style={{
                          backgroundColor: isDark ? "#1e293b" : "#ffffff",
                          color: isDark ? "#f1f5f9" : "#0f172a",
                        }}
                      >
                        {fo.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Close Drawer Button */}
              <div style={{ alignSelf: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setIsFilterExpanded(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "5px 10px",
                    borderRadius: "8px",
                    border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.15)" : "#cbd5e1"
                      }`,
                    backgroundColor: isDark
                      ? "rgba(255, 255, 255, 0.05)"
                      : "#f8fafc",
                    color: isDark ? "#94a3b8" : "#64748b",
                    fontSize: "12px",
                    fontWeight: 600,
                    cursor: "pointer",
                    height: "30px",
                  }}
                >
                  <CloseIcon style={{ fontSize: "14px" }} />
                  <span>Done</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Modern Leaflet Map */}
      <MapContainer
        center={defaultPosition}
        zoom={14}
        style={{ height: "100%", width: "100%" }}
        zoomControl={false}
      >
        <TileLayer
          url={
            isDark
              ? "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          }
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <UpdateMapCenter coordinates={firstValidCoordinate} />

        {CustomerData.map((cust, idx) => {
          const badgeCfg = getStatusBadgeConfig(cust.isApproved);
          return (
            <Marker
              key={`${cust.CustomerId}-${idx}`}
              position={cust.coordinates}
              icon={getModernMarkerIcon(badgeCfg.color, cust.isApproved)}
            >
              <Popup className="custom-leaflet-popup">
                <div
                  style={{
                    padding: "6px",
                    minWidth: "210px",
                    fontFamily: "inherit",
                    color: "#0f172a",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "8px",
                      gap: "6px",
                    }}
                  >
                    <Typography
                      style={{
                        fontWeight: 700,
                        fontSize: "14px",
                        color: "#0f172a",
                        lineHeight: 1.2,
                      }}
                    >
                      {cust.customerFirstName} {cust.customerLastName}
                    </Typography>
                    <span
                      style={{
                        backgroundColor: badgeCfg.bgColor,
                        color: badgeCfg.color,
                        border: `1px solid ${badgeCfg.borderColor}`,
                        borderRadius: "12px",
                        padding: "2px 8px",
                        fontSize: "10px",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {badgeCfg.label}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      fontSize: "12px",
                      color: "#475569",
                      marginBottom: "12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <PhoneIcon style={{ fontSize: "14px", color: "#64748b" }} />
                      <span>{cust.customerPhone}</span>
                    </div>
                    {cust.branchName && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <BusinessIcon
                          style={{ fontSize: "14px", color: "#64748b" }}
                        />
                        <span>
                          {cust.branchName}
                          {cust.regionName ? ` (${cust.regionName})` : ""}
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleView360(cust.CustomerId)}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      backgroundColor: "#2563eb",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "12px",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      boxShadow: "0 2px 6px rgba(37, 99, 235, 0.35)",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#1d4ed8";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#2563eb";
                    }}
                  >
                    <span>View Customer 360</span>
                    <ArrowForwardIcon style={{ fontSize: "14px" }} />
                  </button>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Floating Glassmorphic Legend */}
      <div
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          bottom: "18px",
          right: "18px",
          zIndex: 1000,
          background: isDark
            ? "rgba(15, 23, 42, 0.9)"
            : "rgba(255, 255, 255, 0.94)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderRadius: "12px",
          border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(226, 232, 240, 0.9)"
            }`,
          boxShadow: isDark
            ? "0 8px 24px rgba(0, 0, 0, 0.45)"
            : "0 8px 24px rgba(15, 23, 42, 0.12)",
          padding: isLegendOpen ? "12px 14px" : "5px 10px",
          minWidth: isLegendOpen ? "180px" : "auto",
          transition: "all 0.2s ease",
        }}
      >
        {isLegendOpen ? (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "8px",
                gap: "10px",
              }}
            >
              <Typography
                variant="subtitle2"
                style={{
                  fontWeight: 700,
                  fontSize: "11px",
                  color: isDark ? "#f1f5f9" : "#0f172a",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Status Legend
              </Typography>
              <IconButton
                size="small"
                onClick={() => setIsLegendOpen(false)}
                style={{
                  padding: "2px",
                  color: isDark ? "#94a3b8" : "#64748b",
                }}
              >
                <CloseIcon style={{ fontSize: "14px" }} />
              </IconButton>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: "12px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      backgroundColor: "#10b981",
                      boxShadow: "0 0 6px rgba(16, 185, 129, 0.6)",
                      display: "inline-block",
                    }}
                  />
                  <span
                    style={{
                      color: isDark ? "#e2e8f0" : "#334155",
                      fontWeight: 500,
                      fontSize: "12px",
                    }}
                  >
                    Approved
                  </span>
                </div>
                <span
                  style={{
                    color: isDark ? "#94a3b8" : "#64748b",
                    fontWeight: 600,
                    fontSize: "11px",
                  }}
                >
                  {approvedCount}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: "12px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      backgroundColor: "#f59e0b",
                      boxShadow: "0 0 6px rgba(245, 158, 11, 0.6)",
                      display: "inline-block",
                    }}
                  />
                  <span
                    style={{
                      color: isDark ? "#e2e8f0" : "#334155",
                      fontWeight: 500,
                      fontSize: "12px",
                    }}
                  >
                    Awaiting Approval
                  </span>
                </div>
                <span
                  style={{
                    color: isDark ? "#94a3b8" : "#64748b",
                    fontWeight: 600,
                    fontSize: "11px",
                  }}
                >
                  {pendingCount}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div
            onClick={() => setIsLegendOpen(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#10b981",
                  display: "inline-block",
                }}
              />
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: isDark ? "#e2e8f0" : "#334155",
                }}
              >
                {approvedCount}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#f59e0b",
                  display: "inline-block",
                }}
              />
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: isDark ? "#e2e8f0" : "#334155",
                }}
              >
                {pendingCount}
              </span>
            </div>
            <span
              style={{
                fontSize: "10.5px",
                color: isDark ? "#94a3b8" : "#64748b",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
              }}
            >
              Legend
              <ExpandLessIcon style={{ fontSize: "14px", marginLeft: "1px" }} />
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
export default MapComponent;